// Platform-admin API types (Phase 5). Consumed by src/utils/remote.tsx and the Admin pages.

export type AdminRole = 'student' | 'teacher' | 'admin' | 'school_admin' | 'counselor' | 'staff';

export type AdminPlatformStats = {
    generated_at: string;
    institutions: {
        total: number;
        by_type: { school: number; college: number; university: number };
        /** Created in the last 30 days (all types); absent on older servers. */
        new_30d?: number;
        /** Institutions with a student result in the last 14 days; absent on older servers. */
        active_window?: number;
    };
    users: {
        total: number;
        active: number;
        superusers: number;
        by_role: Record<AdminRole, number>;
    };
    students: {
        total: number;
        joined_30d: number;
        active_7d: number;
        inactive_30d: number;
        subscribed: number;
        /** Distinct students with a result 7 to 14 days ago. */
        active_prev_7d?: number;
        /** Students without a result 30 to 60 days ago who already existed 30 days ago. */
        inactive_prev_30d?: number;
        inactive_top_school?: { id: number; name: string; n: number } | null;
    };
    exams: { total: number; national: number; published: number; draft: number; draft_national: number };
    results: { last_30d: number; prev_30d: number };
    ai: {
        users: number;
        input_tokens: number;
        output_tokens: number;
        total_spent_usd: string;
        monthly_spent_usd: string;
        credits_used_month?: number;
        credits_allocation_total?: number;
        /** Schools at 90% or more of this month's credit allocation. */
        schools_over_90?: number;
    };
    growth: { month: string; students_joined: number; results: number; active?: number }[];
    recent_signups: {
        id: number;
        username: string;
        name: string;
        role: string | null;
        institution_name: string | null;
        date_joined: string;
        is_active: boolean;
        is_superuser?: boolean;
    }[];
    most_active_schools: { id: number; name: string; students: number; active_7d: number }[];
    attention: { institution_id: number; name: string; reason: string; detail: string }[];
    /** Distinct schools flagged before the list is capped; absent on older servers. */
    attention_total?: number;
};

export type AdminStudentStatus = 'active' | 'inactive' | 'subscribed' | 'free';
export type AdminStudentSort = 'name' | '-name' | 'last_active' | '-last_active' | 'average';

export type AdminStudentsParams = {
    school?: number | string;
    grade?: number | string;
    search?: string;
    status?: AdminStudentStatus;
    sort?: AdminStudentSort;
    page?: number;
    page_size?: number;
};

export type AdminStudentRow = {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    institution_id: number | null;
    institution_name: string | null;
    grade_id: number | null;
    grade_name: string | null;
    section: string | null;
    results_count: number;
    average: number | null;
    last_result_at: string | null;
    subscribed: boolean;
};

export type AdminStudentsPage = {
    count: number;
    page: number;
    page_size: number;
    results: AdminStudentRow[];
};

export type AdminExamPatch = Partial<{ status: 'Active' | 'Draft'; name: string; time_minutes: number; exam_year: number | null }>;

export type AdminSchoolAIUsageRow = {
    monthly_spent_usd: number;
    period_start: string | null;
    institution_id: number | null;
    institution_name: string | null;
    institution_plan?: AdminSchoolPlan;
    [key: string]: any; // existing fields pass through unchanged
};

export type AdminSchoolPlan = 'pilot' | 'standard' | 'premium';
export const ADMIN_SCHOOL_PLANS: AdminSchoolPlan[] = ['pilot', 'standard', 'premium'];

/** Default monthly AI credits per plan; mirrors backend PLAN_AI_CREDITS. */
export const PLAN_AI_CREDITS: Record<AdminSchoolPlan, number> = { pilot: 5000, standard: 15000, premium: 40000 };

export type AdminPushStatus = 'sent' | 'partial' | 'failed' | 'no_recipients';

export type AdminPushBroadcast = {
    id: number;
    title: string;
    body: string;
    data: any;
    filters: Record<string, any>;
    audience_label: string;
    users_matched: number;
    tokens_targeted: number;
    success_count: number;
    failure_count: number;
    status: AdminPushStatus;
    error: string;
    sent_by: number | null;
    sent_by_username: string | null;
    created: string;
};

export type AdminPushBroadcastsPage = {
    count: number;
    page: number;
    page_size: number;
    results: AdminPushBroadcast[];
    /** Every registered FCM device token, platform-wide. Absent on older servers. */
    devices_registered?: number;
};

export type AdminAiCreditsSchool = {
    institution_id: number;
    name: string;
    plan: AdminSchoolPlan;
    allocation: number;
    credits_used: number;
    spent_usd: number | string;
    last_usage: string | null;
    /** Lifetime token sums of the school's users (no monthly figure is kept). Absent on older servers. */
    input_tokens_total?: number;
    output_tokens_total?: number;
};

export type AdminAiCredits = {
    credits_per_usd: number;
    month_start: string;
    schools: AdminAiCreditsSchool[];
    totals: { allocation: number; credits_used: number; spent_usd: number | string };
};

export type AdminCoverageStatus = 'published' | 'draft' | 'missing';

export type AdminCoverageLevel = 'school' | 'university';

export type AdminNationalCoverage = {
    level?: AdminCoverageLevel;
    years: number[];
    subjects: { subject: string; cells: { status: AdminCoverageStatus; exam_ids: number[] }[] }[];
    unassigned_count: number;
};

// Account sharing protection (api/device_binding/admin_views.py).
export type AccountSignalStatus = 'open' | 'reviewed' | 'dismissed';

export type AccountSignalRow = {
    id: number;
    user_id: number;
    name: string;
    username: string;
    kind: string;
    severity: 'warn' | 'info';
    status: AccountSignalStatus;
    /** How many times the rule was seen firing, not a number of separate incidents. */
    count: number;
    times_seen: number;
    first_at: string | null;
    last_at: string | null;
    summary: string;
    note?: string;
    reviewed_by?: number | null;
    reviewed_at?: string | null;
};

export type AccountSignalsParams = {
    status?: AccountSignalStatus | 'all';
    kind?: string;
    q?: string;
    page?: number;
    page_size?: number;
};

export type AccountSignalsPage = {
    count: number;
    page: number;
    page_size: number;
    results: AccountSignalRow[];
    counts: Record<AccountSignalStatus, number>;
};

export type UserDevice = {
    id: number;
    platform: string;
    model: string;
    app_version: string;
    protection: string;
    first_seen: string | null;
    last_seen: string | null;
    last_ip_prefix: string;
    content_reset_at: string | null;
    is_active: boolean;
};

export type UserDeviceSession = {
    id: string;
    device_id: number;
    created: string | null;
    revoked_at: string | null;
    revoked_reason: string;
    last_seen: string | null;
    is_active: boolean;
};

export type UserDeviceEvent = {
    id: number;
    kind: string;
    device_id: number | null;
    ip_prefix: string;
    detail: unknown;
    created: string | null;
};

export type UserDevicesDetail = {
    user: { id: number; name: string; username: string };
    devices: UserDevice[];
    sessions: UserDeviceSession[];
    events: UserDeviceEvent[];
    switches_used: number;
    switch_limit: number;
    window_days: number;
    next_free_at: string | null;
    bypass_until: string | null;
    signals: AccountSignalRow[];
};

export type DeviceAction = 'reset-switches' | 'sign-out-all' | 'allow-new-device' | 'move-downloads';

/** Every action returns the refreshed detail under `state`, plus an action-specific field. */
export type DeviceActionResult = {
    state: UserDevicesDetail;
    revoked?: number;
    bypass_until?: string | null;
    moved?: number;
};

export type DeviceBindingMode = 'off' | 'grace' | 'strict';

export type DeviceSettings = {
    device_binding_mode: DeviceBindingMode;
    device_switch_limit: number;
    device_switch_window_days: number;
    device_new_device_code: boolean;
    device_signout_upload_grace_days: number;
    device_signals_to_schools: boolean;
    support_phone: string;
};

export type StudentDeviceSummary = { switches_30d: number; open_flags: number };
